import ControlFactory from '@/Factories/ControlFactory';
import RADAplication from '@/Management/RADApplication';
import ComponentManager from '@/Management/ComponentManager';
import LinkTextBoxViewModel from '@/ViewModels/Controls/LinkTextBoxViewModel';
import LinkTextBox from '@/Controls/LinkTextBox';
import TextBoxViewModel from '@/ViewModels/Controls/TextBoxViewModel';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import LocatorHelper from '@/Utility/LocatorHelper';
import Utilities from '@/Utility/Utilities';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

export default class LinkTextBoxFactory extends ControlFactory {

    private jQueryControl: JQuery;
    private LinkTextBoxViewModel = ComponentManager.getViewModel<typeof LinkTextBoxViewModel>('LinkTextBoxViewModel');
    private LinkTextBox = ComponentManager.getControl<typeof LinkTextBox>('LinkTextBox');
    private TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModel>('TextBoxViewModel');
    protected LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModel>('LinkViewModel');
    protected ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');

    private value: KnockoutObservable<string>;
    private isReadMode: KnockoutObservable<boolean>;

    /**
     * @override
     */
    public viewModel: LinkTextBoxViewModel;

    public constructor(params: any) {
        super(params);
        this.value = this.context.genericObject.getObservable(this.detailViewItem.name);
        this.isReadMode = this.observableOrDefault(this.isSummaryViewMode);
    }

    /**
     * @override
     */
    control: LinkTextBox;

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.viewModel = this.createViewModel();

        this.control = new this.LinkTextBox(this.viewModel);

        let tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        this.addOverwritableToControl(this.jQueryControl, [this.detailViewItem.name]);
        this.registerDefaultValidation([this.detailViewItem.name]);
        this.registerCustomValidation([this.detailViewItem.name], this.value, (newValue: any): boolean => {
            if (!this.isVisible() || newValue === undefined || newValue === null) {
                return true;
            }

            newValue = newValue.trim();
            // wenn aus "Eigenschaften(Windows)" kopiert wird, dann kann dieses unsichtbare  Sonderzeichen vorne stehen.
            if (newValue.length > 0 && newValue.charCodeAt(0) == 8234)
                newValue = newValue.substring(1);

            let regex = /^[a-zA-Z]:[\/|\\].*/g;
            let erg = regex.exec(newValue);

            return erg == null;
        })

        return this.jQueryControl;
    }

    private createViewModel(): LinkTextBoxViewModel {
        let length = this.detailViewItem.length;
        if (isNaN(length) || length === 0) {
            length = 255;
        }

        return new this.LinkTextBoxViewModel({
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            textBox: new this.TextBoxViewModel({
                value: this.value,
                isVisible: this.defineComputed(() => {
                    return !this.isReadMode();
                }),
                isValid: this.isValid,
                isEnabled: this.isEnabled,
                isFocusable: this.isFocusable,
                invalidationText: this.invalidationText,
                maxLength: length,
                parentLocator: this.locator,
                locator: this.detailViewItem.name,
                tooltip: new this.LabelViewModel({
                    ergName: this.detailViewItem.tooltip
                })
            }),
            link: new this.LinkViewModel({
                isExtern: true,
                parentLocator: this.locator,
                locator: LocatorHelper.buildLocator(this.detailViewItem.name, 'open'),
                label: new this.LabelViewModel({
                    parentLocator: this.locator,
                    locator: LocatorHelper.buildLocator(this.detailViewItem.name, 'open.label'),
                    caption: this.value
                }),
                isVisible: this.isReadMode,
                href: this.value
            }),
            linkChange: new this.LinkViewModel({
                parentLocator: this.locator,
                locator: LocatorHelper.buildLocator(this.detailViewItem.name, 'change'),
                image: new this.ImageViewModel({
                    parentLocator: this.locator,
                    locator: LocatorHelper.buildLocator(this.detailViewItem.name, 'change.image'),
                    source: GlobalConfiguration.inlineImages.CHANGE_VIEW,
                    alternativeText: new this.LabelViewModel({
                        ergName: 'FACTORY_LINKTEXTBOX_CHANGE_VIEW'
                    }),
                }),
                onClick: () => {
                    this.jQueryControl.children('.wrapper').toggle(this.isReadMode())
                    this.isReadMode.toggle();
                },
                tooltip: new this.LabelViewModel({
                    ergName: 'FACTORY_LINKTEXTBOX_CHANGE_VIEW'
                }),
                isEnabled: this.defineComputed(() => {
                    return !!this.value();
                }),
                isVisible: this.defineComputed(() => {
                    return !this.isSummaryViewMode;
                }),
                isFocusable: this.isFocusable,
                isToolBarLink: true
            })
        });
    }
}

ComponentManager.setFactory('LinkTextBox', LinkTextBoxFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');